import { getPresidentialProfile } from './features/presidents/registry.js';

export const SITE_ORIGIN = 'https://www.vitrinedepolitico.com.br';
const SITE_NAME = 'Vitrine de Político';
const SITE_DESCRIPTION = 'Dados públicos sobre representantes, candidaturas e eleições no Brasil, com fontes oficiais e limites de cobertura indicados.';
const DEFAULT_IMAGE = `${SITE_ORIGIN}/static/icon.svg`;

export interface SeoEntity {
  type?: 'person' | 'party';
  name: string;
  role?: string | null;
  party?: string | null;
  state?: string | null;
  year?: number | null;
  description?: string | null;
  image?: string | null;
  sourceUrl?: string | null;
}

export interface PageSeo {
  title: string;
  description: string;
  canonical: string;
  robots: string;
  image: string;
  jsonLd: Record<string, unknown>;
}

interface PageCopy { title: string; description: string }

const pages: Record<string, PageCopy> = {
  '/apuracao_eleicoes_2026_segundo_turno': {
    title: 'Apuração eleitoral 2026: resultados do 2º turno',
    description: 'Acompanhe os resultados do segundo turno das eleições de 2026 por estado e município, com dados publicados pelo Tribunal Superior Eleitoral.',
  },
  '/apuracao_eleicoes_2026_primeiro_turno': {
    title: 'Resultados eleitorais de 2026: primeiro turno',
    description: 'Consulte a apuração do primeiro turno das eleições de 2026 por estado e município, com origem e cobertura dos dados do TSE.',
  },
  '/politicos': {
    title: 'Políticos do Brasil: perfis, mandatos e atuação',
    description: 'Pesquise representantes brasileiros por nome, partido, cargo e estado. Consulte mandatos, votações, proposições, despesas e fontes oficiais.',
  },
  '/partidos': {
    title: 'Partidos políticos do Brasil: siglas e representantes',
    description: 'Explore partidos e siglas políticas brasileiras e consulte os representantes associados a cada legenda.',
  },
  '/candidaturas': {
    title: 'Candidaturas eleitorais no Brasil: registros do TSE',
    description: 'Consulte registros de candidaturas à Presidência e a cargos federais, estaduais e municipais, com dados eleitorais e fontes do TSE.',
  },
  '/presidentes': {
    title: 'Presidentes do Brasil: acervo histórico e despesas do CPGF',
    description: 'Consulte o acervo oficial de presidentes do Brasil e os valores institucionais do Cartão de Pagamento da Presidência, com períodos e limites de cobertura identificados.',
  },
  '/comparar': {
    title: 'Compare representantes políticos',
    description: 'Compare representantes do mesmo cargo usando registros públicos de mandatos, votações, proposições e despesas, com cobertura indicada.',
  },
  '/impostometro': {
    title: 'Impostômetro: arrecadação federal por estado e tributo',
    description: 'Acompanhe a arrecadação federal publicada pela Receita Federal, com valores por período, tributo e Unidade da Federação.',
  },
  '/fontes': {
    title: 'Fontes oficiais de dados políticos',
    description: 'Consulte as fontes oficiais usadas para dados de representantes, eleições, candidaturas, atividade parlamentar e arrecadação.',
  },
  '/metodologia': {
    title: 'Metodologia, cobertura e limites dos dados',
    description: 'Entenda as fontes, critérios de associação, cobertura e limites dos dados públicos apresentados pela Vitrine de Político.',
  },
  '/privacidade': {
    title: 'Privacidade e publicidade',
    description: 'Saiba como a Vitrine de Político trata dados públicos, métricas agregadas de navegação e espaços de publicidade.',
  },
};

function normalizedPath(pathname: string) {
  const path = new URL(pathname || '/', SITE_ORIGIN).pathname.replace(/\/{2,}/g, '/').replace(/\/$/, '') || '/';
  if (path === '/') return '/apuracao_eleicoes_2026_segundo_turno';
  if (path === '/explorar') return '/politicos';
  if (path === '/comparacao') return '/comparar';
  if (path === '/apuracao') return '/apuracao_eleicoes_2026_segundo_turno';
  if (path === '/apuracao_eleicoes_2026_segundo_turno_backup') return '/apuracao_eleicoes_2026_segundo_turno';
  const legacyProfile = path.match(/^\/politicos\/(\d+)$/);
  if (legacyProfile) return `/perfil/${legacyProfile[1]}`;
  return path;
}

function routeKnown(path: string) {
  const presidentProfileSlug = path.match(/^\/presidentes\/([^/]+)$/)?.[1];
  return path in pages || path === '/dashboard' || path === '/presidentes/comparar' || Boolean(presidentProfileSlug && getPresidentialProfile(presidentProfileSlug)) || /^\/perfil\/\d+$/.test(path) || /^\/candidaturas\/\d+$/.test(path)
    || /^\/partidos\/[^/]+$/.test(path) || /^\/apuracao_eleicoes_2026_segundo_turno\/[a-z]{2}$/i.test(path);
}

export function isKnownSeoPath(pathname: string) {
  return routeKnown(normalizedPath(pathname));
}

function absoluteImage(value?: string | null) {
  if (!value) return DEFAULT_IMAGE;
  try {
    const url = new URL(value, SITE_ORIGIN);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : DEFAULT_IMAGE;
  } catch { return DEFAULT_IMAGE; }
}

function breadcrumbs(path: string, entity?: SeoEntity) {
  const items = [{ name: 'Início', url: `${SITE_ORIGIN}/apuracao_eleicoes_2026_segundo_turno` }];
  if (path.startsWith('/perfil/')) {
    items.push({ name: 'Políticos', url: `${SITE_ORIGIN}/politicos` }, { name: entity?.name ?? 'Perfil público', url: `${SITE_ORIGIN}${path}` });
  } else if (path.startsWith('/partidos/')) {
    items.push({ name: 'Partidos', url: `${SITE_ORIGIN}/partidos` }, { name: entity?.name ?? 'Partido político', url: `${SITE_ORIGIN}${path}` });
  } else if (path.startsWith('/candidaturas/')) {
    items.push({ name: 'Candidaturas', url: `${SITE_ORIGIN}/candidaturas` }, { name: entity?.name ?? 'Registro de candidatura', url: `${SITE_ORIGIN}${path}` });
  } else if (path.startsWith('/presidentes/')) {
    items.push({ name: 'Presidentes', url: `${SITE_ORIGIN}/presidentes` }, { name: path === '/presidentes/comparar' ? 'Comparar presidentes' : entity?.name ?? 'Perfil presidencial', url: `${SITE_ORIGIN}${path}` });
  } else if (path in pages && path !== '/apuracao_eleicoes_2026_segundo_turno') {
    items.push({ name: pages[path]?.title ?? SITE_NAME, url: `${SITE_ORIGIN}${path}` });
  }
  return items;
}

export function createPageSeo(pathname: string, search = '', entity?: SeoEntity): PageSeo {
  const path = normalizedPath(pathname);
  const canonical = `${SITE_ORIGIN}${path}`;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const known = routeKnown(path);
  const isDashboard = path === '/dashboard';
  const isProfile = /^\/perfil\/\d+$/.test(path);
  const isCandidacy = /^\/candidaturas\/\d+$/.test(path);
  const isParty = /^\/partidos\/[^/]+$/.test(path);
  const isPresidentialComparison = path === '/presidentes/comparar';
  const presidentSlug = path.match(/^\/presidentes\/([^/]+)$/)?.[1];
  const presidentialProfile = presidentSlug ? getPresidentialProfile(presidentSlug) : null;
  const isPresidentialProfile = Boolean(presidentialProfile);
  let title = pages[path]?.title ?? (isDashboard ? 'Painel administrativo' : isProfile ? `Perfil público de político brasileiro | ${SITE_NAME}` : isCandidacy ? `Candidatura eleitoral | ${SITE_NAME}` : isParty ? `Partido político | ${SITE_NAME}` : isPresidentialComparison ? `Comparar presidentes | ${SITE_NAME}` : isPresidentialProfile ? `${presidentialProfile?.name} | ${SITE_NAME}` : 'Página não encontrada');
  let description = pages[path]?.description ?? (isProfile ? 'Consulte mandatos, atuação e registros públicos de representantes brasileiros, com fontes oficiais e cobertura indicada.' : isCandidacy ? 'Consulte dados oficiais de candidatura, situação eleitoral e informações declaradas ao Tribunal Superior Eleitoral.' : isParty ? 'Consulte siglas partidárias brasileiras e os representantes associados a cada partido.' : isPresidentialComparison ? 'Compare períodos e despesas institucionais com CPGF publicadas por fontes oficiais, respeitando a cobertura e os valores conjuntos.' : isPresidentialProfile ? `Consulte a biografia oficial e os dados públicos disponíveis para ${presidentialProfile?.name}, com os limites do CPGF indicados.` : SITE_DESCRIPTION);

  if (isProfile && entity?.name) {
    const roleAndPlace = [entity.role, entity.state].filter(Boolean).join(' · ');
    title = `${entity.name}${roleAndPlace ? `: ${roleAndPlace}` : ''} | ${SITE_NAME}`;
    description = entity.description?.trim() || `Consulte o perfil público de ${entity.name}${roleAndPlace ? `, ${roleAndPlace}` : ''}: mandatos, atuação, registros e fontes oficiais.`;
  } else if (isCandidacy && entity?.name) {
    title = `${entity.name}: candidatura ${entity.year ?? ''} ${entity.role ?? ''} | ${SITE_NAME}`.replace(/\s+/g, ' ').trim();
    description = entity.description?.trim() || `Consulte o registro eleitoral de ${entity.name}${entity.year ? ` nas eleições de ${entity.year}` : ''}${entity.role ? ` para ${entity.role}` : ''}${entity.state ? ` em ${entity.state}` : ''}, conforme os dados publicados pelo TSE.`;
  } else if (isParty && entity?.name) {
    title = `${entity.name}: partido político e representantes | ${SITE_NAME}`;
    description = entity.description?.trim() || `Consulte informações públicas sobre ${entity.name}, sigla partidária e representantes cadastrados na Vitrine de Político.`;
  }

  const descriptionText = [...description].slice(0, 300).join('');
  const robots = !known ? 'noindex, nofollow' : isDashboard ? 'noindex, nofollow' : params.size ? 'noindex, follow' : 'index, follow';
  const image = absoluteImage(entity?.image);
  const crumbs = breadcrumbs(path, entity);
  const mainEntity = entity?.name ? {
    '@type': entity.type === 'party' ? 'Organization' : 'Person',
    '@id': `${canonical}#entity`,
    name: entity.name,
    ...(entity.role ? { jobTitle: entity.role } : {}),
    ...(entity.description ? { description: [...entity.description].slice(0, 300).join('') } : {}),
    ...(entity.sourceUrl ? { sameAs: [entity.sourceUrl] } : {}),
    ...(entity.image ? { image } : {}),
  } : undefined;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'Organization', '@id': `${SITE_ORIGIN}/#organization`, name: SITE_NAME, url: SITE_ORIGIN, logo: DEFAULT_IMAGE },
      { '@type': 'WebSite', '@id': `${SITE_ORIGIN}/#website`, name: SITE_NAME, url: SITE_ORIGIN, inLanguage: 'pt-BR', publisher: { '@id': `${SITE_ORIGIN}/#organization` } },
      { '@type': 'WebPage', '@id': `${canonical}#webpage`, name: title, description: descriptionText, url: canonical, inLanguage: 'pt-BR', isPartOf: { '@id': `${SITE_ORIGIN}/#website` }, ...(mainEntity ? { mainEntity } : {}) },
      { '@type': 'BreadcrumbList', itemListElement: crumbs.map((item, index) => ({ '@type': 'ListItem', position: index + 1, name: item.name, item: item.url })) },
    ],
  };
  return { title, description: descriptionText, canonical, robots, image, jsonLd };
}

function escapeAttribute(value: string) {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function renderSeoHead(seo: PageSeo) {
  const jsonLd = JSON.stringify(seo.jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');
  const title = escapeAttribute(seo.title);
  const description = escapeAttribute(seo.description);
  const canonical = escapeAttribute(seo.canonical);
  const robots = escapeAttribute(seo.robots);
  const image = escapeAttribute(seo.image);
  return `<title>${title}</title>\n    <meta name="description" content="${description}" />\n    <meta name="robots" content="${robots}" />\n    <link rel="canonical" href="${canonical}" />\n    <meta property="og:type" content="website" />\n    <meta property="og:site_name" content="${SITE_NAME}" />\n    <meta property="og:title" content="${title}" />\n    <meta property="og:description" content="${description}" />\n    <meta property="og:url" content="${canonical}" />\n    <meta property="og:image" content="${image}" />\n    <meta name="twitter:card" content="summary_large_image" />\n    <meta name="twitter:title" content="${title}" />\n    <meta name="twitter:description" content="${description}" />\n    <meta name="twitter:image" content="${image}" />\n    <script type="application/ld+json">${jsonLd}</script>`;
}

export function applyClientSeo(seo: PageSeo) {
  document.title = seo.title;
  const setMeta = (selector: string, attribute: 'content' | 'href', value: string) => {
    document.querySelector(selector)?.setAttribute(attribute, value);
  };
  setMeta('meta[name="description"]', 'content', seo.description);
  setMeta('meta[name="robots"]', 'content', seo.robots);
  setMeta('link[rel="canonical"]', 'href', seo.canonical);
  setMeta('meta[property="og:title"]', 'content', seo.title);
  setMeta('meta[property="og:description"]', 'content', seo.description);
  setMeta('meta[property="og:url"]', 'content', seo.canonical);
  setMeta('meta[property="og:image"]', 'content', seo.image);
  setMeta('meta[name="twitter:title"]', 'content', seo.title);
  setMeta('meta[name="twitter:description"]', 'content', seo.description);
  setMeta('meta[name="twitter:image"]', 'content', seo.image);
  const structuredData = document.querySelector('script[type="application/ld+json"]');
  if (structuredData) structuredData.textContent = JSON.stringify(seo.jsonLd);
}
